/**
 * Blog Carousel - JavaScript
 * Handles Swiper initialization and responsive behavior
 * Consistent with collection-carousel.js patterns
 */

(function () {
  "use strict";

  var swiperInstances = new Map();

  function ensureSwiper(sectionElement, callback) {
    if (window.Swiper) {
      callback();
      return;
    }

    var scriptSrc = sectionElement.dataset.swiperSrc;
    var existingScript = document.querySelector(
      'script[src*="swiper-bundle.min.js"]',
    );

    if (existingScript) {
      existingScript.addEventListener("load", callback, { once: true });
      return;
    }

    var script = document.createElement("script");
    script.src = scriptSrc;
    script.async = true;
    script.addEventListener("load", callback, { once: true });
    document.head.appendChild(script);
  }

  /**
   * Initialize a blog carousel
   */
  function initBlogCarousel(sectionElement) {
    if (!window.Swiper) {
      ensureSwiper(sectionElement, function () {
        initBlogCarousel(sectionElement);
      });
      return;
    }

    var sectionId = sectionElement.dataset.sectionId;

    // Prevent double initialization on the same DOM element
    if (sectionElement._blogCarouselInitialized) {
      return;
    }

    var carouselElement = sectionElement.querySelector(
      "[data-blog-carousel-swiper]",
    );
    if (!carouselElement) {
      return;
    }

    var carouselKey = sectionId + "-blog-carousel";

    // Check if already in instances map
    if (swiperInstances.has(carouselKey)) {
      return swiperInstances.get(carouselKey);
    }

    var swiperConfig = {
      slidesPerView: "auto",
      slidesPerGroup: 1,
      spaceBetween: 16,
      breakpoints: {
        320: {
          slidesPerGroup: 1,
          slidesPerView: "auto",
          spaceBetween: 12,
        },
        768: {
          slidesPerView: 2,
          slidesPerGroup: 2,
          spaceBetween: 16,
        },
        1025: {
          slidesPerView: 3,
          slidesPerGroup: 3,
          spaceBetween: 16,
        },
      },

      pagination: {
        el: sectionElement.querySelector('[data-blog-pagination]'),
        clickable: true,
        renderBullet: function (index, className) {
          return '<button type="button" class="' + className + '" aria-label="Go to blog page ' + (index + 1) + '"></button>';
        },
      },

      // Performance & tracking
      watchSlidesProgress: true,
      watchOverflow: true,

      // Touch & drag settings
      simulateTouch: true,
      allowTouchMove: true,
      touchRatio: 1,
      resistance: true,
      resistanceRatio: 0.85,
      threshold: 5,
      followFinger: true,
      shortSwipes: true,
      longSwipes: true,
      touchEventsTarget: "container",

      // Layout
      centeredSlides: false,
      updateOnWindowResize: true,
      // End-state classes change during swipes; observe size, not our own attributes.
      resizeObserver: true,

      // Desktop navigation sits beside the section heading.
      navigation: {
        nextEl: sectionElement.querySelector(".blog-carousel__arrow--next"),
        prevEl: sectionElement.querySelector(".blog-carousel__arrow--prev"),
      },

      // Transitions
      speed: 600,
      cssMode: false,

      // Accessibility
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous article",
        nextSlideMessage: "Next article",
      },

      // Events
      on: {
        slideChange: function () { updateNavVisibility(this, carouselElement); },
        progress: function () { updateNavVisibility(this, carouselElement); },
        init: function () {
          updateNavVisibility(this, carouselElement);
        },
        update: function () { updateNavVisibility(this, carouselElement); },
        snapGridLengthChange: function () { updateNavVisibility(this, carouselElement); },
        breakpoint: function () {
          updateNavVisibility(this, carouselElement);
        },
        resize: function () {
          updateNavVisibility(this, carouselElement);
        },
      },
    };

    var blogSwiper = new Swiper(carouselElement, swiperConfig);
    swiperInstances.set(carouselKey, blogSwiper);
    sectionElement._blogCarouselInitialized = true;
  }

  /**
   * Update navigation button visibility
   * Hide when all slides fit in the viewport
   */
  function updateNavVisibility(swiperInstance, carouselElement) {
    if (!swiperInstance || !swiperInstance.initialized) return;
    var section = carouselElement.closest('[data-section-type="blog-carousel"]');
    var pages = Math.max(1, swiperInstance.snapGrid.length);
    var locked = swiperInstance.isLocked || pages <= 1;
    carouselElement.classList.toggle("is-mobile-end", window.innerWidth < 768 && swiperInstance.isEnd && !swiperInstance.isBeginning && !locked);
    var navigation = section.querySelector('[data-blog-navigation]');
    var pagination = section.querySelector('[data-blog-pagination]');
    var progress = section.querySelector('[data-blog-progress]');
    var header = section.querySelector('.blog-carousel__header');
    var previousButton = section.querySelector('.blog-carousel__arrow--prev');
    var nextButton = section.querySelector('.blog-carousel__arrow--next');
    if (navigation) navigation.hidden = locked;
    if (pagination) pagination.hidden = locked;
    if (progress) {
      progress.hidden = locked;
      var thumb = progress.firstElementChild;
      var position = Math.max(0, Math.min(1, swiperInstance.progress));
      thumb.style.width = (100 / pages) + '%';
      thumb.style.transform = 'translateX(' + (position * (pages - 1) * 100) + '%)';
    }
    if (header) header.hidden = !header.querySelector('.blog-carousel__heading') && (locked || window.innerWidth < 768);
    if (previousButton) previousButton.disabled = locked || swiperInstance.isBeginning;
    if (nextButton) nextButton.disabled = locked || swiperInstance.isEnd;
  }

  /**
   * Initialize all blog carousels on the page
   */
  function initAll() {
    var sections = document.querySelectorAll(
      '[data-section-type="blog-carousel"]',
    );
    sections.forEach(function (section) {
      initBlogCarousel(section);
    });
  }

  /**
   * Cleanup all instances
   */
  function cleanupAll() {
    swiperInstances.forEach(function (swiper, key) {
      swiper.destroy(true, false);
    });
    swiperInstances.clear();

    var sections = document.querySelectorAll(
      '[data-section-type="blog-carousel"]',
    );
    sections.forEach(function (section) {
      section._blogCarouselInitialized = false;
    });
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Shopify theme editor support
  document.addEventListener("shopify:section:load", function (event) {
    var section = event.target.querySelector(
      '[data-section-type="blog-carousel"]',
    );
    if (section) {
      initBlogCarousel(section);
    }
  });

  document.addEventListener("shopify:section:unload", function (event) {
    var section = event.target.querySelector(
      '[data-section-type="blog-carousel"]',
    );
    if (section) {
      var sectionId = section.dataset.sectionId;
      var carouselKey = sectionId + "-blog-carousel";

      if (swiperInstances.has(carouselKey)) {
        swiperInstances.get(carouselKey).destroy(true, false);
        swiperInstances.delete(carouselKey);
        section._blogCarouselInitialized = false;
      }
    }
  });

  document.addEventListener("shopify:section:reorder", function () {
    cleanupAll();
    initAll();
  });

  // Handle window resize (debounced)
  var resizeTimeout;
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(function () {
      swiperInstances.forEach(function (swiper) {
        if (swiper && swiper.update) {
          swiper.update();
        }
      });
    }, 250);
  });

  // Cleanup on page unload
  window.addEventListener("beforeunload", cleanupAll);
})();
